<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const VARIABLE_FORMATS = {
  POSITIONAL: 'POSITIONAL',
  NAMED: 'NAMED',
};

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = useMapGetter('inboxes/getUIFlags');

const selectedInboxId = ref(null);
const selectedTemplateKey = ref(null);
const isCampaignUsageLoading = ref(false);
const showOptOutTip = ref(false);
const contactNameVariable = '{{nome}}';
const form = reactive({
  name: '',
  category: 'MARKETING',
  language: 'pt_BR',
  variableFormat: VARIABLE_FORMATS.NAMED,
  variableName: 'nome',
  body: '',
});

const categoryOptions = computed(() => [
  {
    value: 'MARKETING',
    label: t('CAMPAIGN.WHATSAPP.TEMPLATES.CATEGORIES.MARKETING'),
  },
  {
    value: 'UTILITY',
    label: t('CAMPAIGN.WHATSAPP.TEMPLATES.CATEGORIES.UTILITY'),
  },
]);

const languageOptions = computed(() => [
  { value: 'pt_BR', label: t('CAMPAIGN.WHATSAPP.TEMPLATES.LANGUAGES.PT_BR') },
  { value: 'en_US', label: t('CAMPAIGN.WHATSAPP.TEMPLATES.LANGUAGES.EN_US') },
  { value: 'es', label: t('CAMPAIGN.WHATSAPP.TEMPLATES.LANGUAGES.ES') },
]);

const variableFormatOptions = computed(() => [
  {
    value: VARIABLE_FORMATS.NAMED,
    label: t('CAMPAIGN.WHATSAPP.TEMPLATES.VARIABLE_FORMATS.TEXT'),
    icon: 'i-lucide-text-cursor-input',
  },
  {
    value: VARIABLE_FORMATS.POSITIONAL,
    label: t('CAMPAIGN.WHATSAPP.TEMPLATES.VARIABLE_FORMATS.NUMBER'),
    icon: 'i-lucide-list-ordered',
  },
]);

const apiInboxOptions = computed(() =>
  inboxes.value
    .filter(
      inbox =>
        inbox.channel_type === INBOX_TYPES.API &&
        inbox.additional_attributes?.is_evolution_api_integration
    )
    .map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const selectedInbox = computed(() =>
  inboxes.value.find(inbox => inbox.id === Number(selectedInboxId.value))
);

const existingTemplates = computed(
  () => selectedInbox.value?.additional_attributes?.message_templates || []
);

const campaigns = computed(() => store.getters['campaigns/getAllCampaigns']);
const isSaving = computed(() => uiFlags.value.isUpdating);
const isEditing = computed(() => selectedTemplateKey.value !== null);

const normalizedTemplateName = computed(() =>
  form.name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
);

const canSave = computed(
  () =>
    selectedInboxId.value &&
    normalizedTemplateName.value &&
    form.body.trim().length > 0
);

const templateVariables = computed(() => {
  const variables = new Set();
  const matches = form.body.match(/\{\{\s*([^}]+?)\s*\}\}/g) || [];

  matches.forEach(match => {
    const variable = match.replace(/[{}]/g, '').trim();
    if (variable) variables.add(variable);
  });

  return [...variables];
});

const variableExample = variable => {
  if (variable.toLowerCase() === 'nome') return 'Maria Oliveira';
  if (/^\d+$/.test(variable)) return `Exemplo ${variable}`;
  return `Exemplo ${variable}`;
};

const previewMessage = computed(() => {
  const text =
    form.body.trim() || t('CAMPAIGN.WHATSAPP.TEMPLATES.PREVIEW_EMPTY');
  return text.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, variable) =>
    variableExample(variable.trim())
  );
});

const buildBodyComponent = () => {
  const component = {
    type: 'BODY',
    text: form.body.trim(),
  };

  if (!templateVariables.value.length) return component;

  if (form.variableFormat === VARIABLE_FORMATS.NAMED) {
    component.example = {
      body_text_named_params: templateVariables.value.map(variable => ({
        param_name: variable,
        example: variableExample(variable),
      })),
    };
  } else {
    const sortedVariables = [...templateVariables.value].sort(
      (first, second) => Number(first) - Number(second)
    );
    component.example = {
      body_text: [sortedVariables.map(variable => variableExample(variable))],
    };
  }

  return component;
};

const buildTemplate = () => ({
  id: `${normalizedTemplateName.value}_${form.language}`,
  name: normalizedTemplateName.value,
  language: form.language,
  category: form.category,
  parameter_format: form.variableFormat,
  status: 'APPROVED',
  components: [buildBodyComponent()],
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
});

const templateKey = template =>
  `${template.name}::${(template.language || '').toLowerCase()}`;

const selectedTemplate = computed(() =>
  existingTemplates.value.find(
    template => templateKey(template) === selectedTemplateKey.value
  )
);

const nonFinishedCampaignsForTemplate = template => {
  if (!template || !selectedInboxId.value) return [];

  return campaigns.value.filter(campaign => {
    const templateParams = campaign.template_params || {};
    const templateLanguage = template.language?.toLowerCase();
    const campaignLanguage = templateParams.language?.toLowerCase();
    const sameInbox =
      Number(campaign.inbox?.id) === Number(selectedInboxId.value);
    const sameTemplate =
      templateParams.name === template.name &&
      (!templateLanguage ||
        !campaignLanguage ||
        campaignLanguage === templateLanguage);
    const isFinished = campaign.campaign_status === 'completed';

    return sameInbox && sameTemplate && !isFinished;
  });
};

const hasNonFinishedCampaigns = template =>
  nonFinishedCampaignsForTemplate(template).length > 0;

const resetForm = () => {
  selectedTemplateKey.value = null;
  form.name = '';
  form.category = 'MARKETING';
  form.language = 'pt_BR';
  form.variableFormat = VARIABLE_FORMATS.NAMED;
  form.variableName = 'nome';
  form.body = '';
};

const updateTemplates = async templates => {
  const additionalAttributes = selectedInbox.value?.additional_attributes || {};

  await store.dispatch('inboxes/updateInbox', {
    id: selectedInboxId.value,
    formData: false,
    channel: {
      additional_attributes: {
        ...additionalAttributes,
        message_templates: templates,
      },
    },
  });
};

const saveTemplate = async () => {
  if (!canSave.value) return;

  const template = buildTemplate();
  const currentTemplate = selectedTemplate.value;
  const isChangingTemplateIdentity =
    currentTemplate &&
    (currentTemplate.name !== template.name ||
      currentTemplate.language?.toLowerCase() !==
        template.language.toLowerCase());

  if (isChangingTemplateIdentity && hasNonFinishedCampaigns(currentTemplate)) {
    useAlert(t('CAMPAIGN.WHATSAPP.TEMPLATES.UPDATE_BLOCKED'));
    return;
  }

  if (currentTemplate?.created_at) {
    template.created_at = currentTemplate.created_at;
  }

  const templates = [
    template,
    ...existingTemplates.value.filter(
      item =>
        templateKey(item) !== selectedTemplateKey.value &&
        !(
          item.name === template.name &&
          item.language?.toLowerCase() === template.language.toLowerCase()
        )
    ),
  ];

  await updateTemplates(templates);

  useAlert(
    t(
      isEditing.value
        ? 'CAMPAIGN.WHATSAPP.TEMPLATES.UPDATE_SUCCESS'
        : 'CAMPAIGN.WHATSAPP.TEMPLATES.SUCCESS'
    )
  );
  resetForm();
};

const editTemplate = template => {
  selectedTemplateKey.value = templateKey(template);
  form.name = template.name || '';
  form.category = template.category || 'MARKETING';
  form.language = template.language || 'pt_BR';
  form.variableFormat = template.parameter_format || VARIABLE_FORMATS.NAMED;
  form.variableName = 'nome';
  form.body =
    template.components?.find(component => component.type === 'BODY')?.text ||
    '';
};

const deleteTemplate = async template => {
  if (isCampaignUsageLoading.value) {
    useAlert(t('CAMPAIGN.WHATSAPP.TEMPLATES.CAMPAIGNS_LOADING'));
    return;
  }

  const associatedCampaigns = nonFinishedCampaignsForTemplate(template);
  if (associatedCampaigns.length) {
    useAlert(
      t('CAMPAIGN.WHATSAPP.TEMPLATES.DELETE_BLOCKED', {
        count: associatedCampaigns.length,
      })
    );
    return;
  }

  if (!window.confirm(t('CAMPAIGN.WHATSAPP.TEMPLATES.DELETE_CONFIRM'))) return;

  await updateTemplates(
    existingTemplates.value.filter(
      item => templateKey(item) !== templateKey(template)
    )
  );

  if (selectedTemplateKey.value === templateKey(template)) {
    resetForm();
  }

  useAlert(t('CAMPAIGN.WHATSAPP.TEMPLATES.DELETE_SUCCESS'));
};

const nextPositionalVariable = () => {
  const numbers = templateVariables.value
    .map(variable => Number(variable))
    .filter(Number.isInteger);

  return String(numbers.length ? Math.max(...numbers) + 1 : 1);
};

const normalizedVariableName = computed(() =>
  form.variableName
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
);

const variableToInsert = computed(() => {
  if (form.variableFormat === VARIABLE_FORMATS.POSITIONAL) {
    return `{{${nextPositionalVariable()}}}`;
  }

  return `{{${normalizedVariableName.value || 'nome'}}}`;
});

const insertVariable = () => {
  const suffix = form.body && !form.body.endsWith(' ') ? ' ' : '';
  form.body = `${form.body}${suffix}${variableToInsert.value}`;
};

const goBack = () => {
  router.push({
    name: 'campaigns_whatsapp_index',
    params: { accountId: route.params.accountId },
  });
};

watch(selectedInboxId, () => resetForm());

onMounted(() => {
  isCampaignUsageLoading.value = true;
  store.dispatch('campaigns/get', { silent: true }).finally(() => {
    isCampaignUsageLoading.value = false;
  });
});
</script>

<template>
  <section class="flex h-full w-full flex-col overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div
        class="mx-auto flex min-h-24 w-full max-w-7xl items-center justify-between gap-3"
      >
        <div class="min-w-0">
          <h1 class="mb-1 text-heading-1 text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.TITLE') }}
          </h1>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.SUBTITLE') }}
          </p>
        </div>
        <Button
          :label="t('CAMPAIGN.WHATSAPP.TEMPLATES.BACK')"
          icon="i-lucide-arrow-left"
          color="slate"
          variant="faded"
          size="sm"
          @click="goBack"
        />
      </div>
    </header>

    <main class="flex-1 overflow-y-auto px-6">
      <div
        class="mx-auto grid w-full max-w-7xl items-start gap-5 py-5 lg:grid-cols-[minmax(0,1fr)_22rem]"
      >
        <form
          class="flex flex-col gap-4 rounded-2xl border border-n-weak bg-n-solid-1 p-5 shadow-sm"
          @submit.prevent="saveTemplate"
        >
          <div class="flex items-center justify-between gap-3">
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{
                isEditing
                  ? t('CAMPAIGN.WHATSAPP.TEMPLATES.EDITING')
                  : t('CAMPAIGN.WHATSAPP.TEMPLATES.NEW')
              }}
            </p>
            <Button
              v-if="isEditing"
              type="button"
              :label="t('CAMPAIGN.WHATSAPP.TEMPLATES.CANCEL_EDIT')"
              icon="i-lucide-x"
              color="slate"
              variant="ghost"
              size="sm"
              @click="resetForm"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.API_CONNECTION') }}
            </label>
            <ComboBox
              v-model="selectedInboxId"
              :options="apiInboxOptions"
              :placeholder="t('CAMPAIGN.WHATSAPP.TEMPLATES.SELECT_CONNECTION')"
              class="[&>div>button]:bg-n-alpha-black2"
            />
          </div>

          <Input
            v-model="form.name"
            :label="t('CAMPAIGN.WHATSAPP.TEMPLATES.NAME')"
            :placeholder="t('CAMPAIGN.WHATSAPP.TEMPLATES.NAME_PLACEHOLDER')"
          />

          <div class="grid gap-3 sm:grid-cols-2">
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.CATEGORY') }}
              </label>
              <ComboBox
                v-model="form.category"
                :options="categoryOptions"
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.LANGUAGE') }}
              </label>
              <ComboBox
                v-model="form.language"
                :options="languageOptions"
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
          </div>

          <TextArea
            v-model="form.body"
            :label="t('CAMPAIGN.WHATSAPP.TEMPLATES.MESSAGE')"
            :placeholder="t('CAMPAIGN.WHATSAPP.TEMPLATES.MESSAGE_PLACEHOLDER')"
            auto-height
            resize
            min-height="10rem"
            max-height="18rem"
          />

          <div
            v-if="form.category === 'MARKETING'"
            class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4"
          >
            <Button
              type="button"
              :label="
                showOptOutTip
                  ? t('CAMPAIGN.WHATSAPP.TEMPLATES.OPT_OUT_TIP_HIDE')
                  : t('CAMPAIGN.WHATSAPP.TEMPLATES.OPT_OUT_TIP_SHOW')
              "
              icon="i-lucide-lightbulb"
              color="teal"
              variant="ghost"
              size="sm"
              class="self-start"
              @click="showOptOutTip = !showOptOutTip"
            />
            <div
              v-if="showOptOutTip"
              class="flex gap-3 rounded-lg border border-n-teal-7 bg-n-teal-3 p-3 text-sm text-n-teal-12"
            >
              <span
                class="i-lucide-message-circle-off mt-0.5 size-5 shrink-0"
              />
              <div>
                <p class="mb-1 font-medium">
                  {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.OPT_OUT_TIP_TITLE') }}
                </p>
                <p class="mb-1 text-xs leading-5 text-n-teal-11">
                  {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.OPT_OUT_TIP_DESCRIPTION') }}
                </p>
                <p class="mb-0 text-xs font-medium text-n-teal-12">
                  {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.OPT_OUT_TIP_EXAMPLE') }}
                </p>
              </div>
            </div>
          </div>

          <div
            class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-alpha-black2 p-3"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <p class="mb-0 text-sm text-n-slate-11">
                {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.VARIABLE_HINT') }}
              </p>
              <div class="flex flex-wrap items-center gap-2">
                <button
                  v-for="option in variableFormatOptions"
                  :key="option.value"
                  type="button"
                  class="flex h-9 items-center gap-2 rounded-lg border px-3 text-sm font-medium"
                  :class="
                    form.variableFormat === option.value
                      ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                      : 'border-n-weak bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-2'
                  "
                  @click="form.variableFormat = option.value"
                >
                  <span :class="option.icon" class="size-4" />
                  <span>{{ option.label }}</span>
                </button>
              </div>
            </div>
            <div class="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
              <Input
                v-if="form.variableFormat === VARIABLE_FORMATS.NAMED"
                v-model="form.variableName"
                :label="t('CAMPAIGN.WHATSAPP.TEMPLATES.VARIABLE_NAME')"
                :placeholder="
                  t('CAMPAIGN.WHATSAPP.TEMPLATES.VARIABLE_NAME_PLACEHOLDER')
                "
              />
              <p
                v-else
                class="mb-0 flex min-h-10 items-center text-sm text-n-slate-11"
              >
                {{
                  t('CAMPAIGN.WHATSAPP.TEMPLATES.NEXT_NUMBER_VARIABLE', {
                    variable: variableToInsert,
                  })
                }}
              </p>
              <Button
                type="button"
                :label="
                  t('CAMPAIGN.WHATSAPP.TEMPLATES.INSERT_VARIABLE', {
                    variable: variableToInsert,
                  })
                "
                icon="i-lucide-braces"
                color="teal"
                variant="faded"
                size="sm"
                class="self-end"
                @click="insertVariable"
              />
            </div>
          </div>

          <div class="flex justify-end">
            <Button
              type="submit"
              :label="
                isEditing
                  ? t('CAMPAIGN.WHATSAPP.TEMPLATES.UPDATE')
                  : t('CAMPAIGN.WHATSAPP.TEMPLATES.SAVE')
              "
              icon="i-lucide-save"
              :is-loading="isSaving"
              :disabled="!canSave || isSaving"
            />
          </div>
        </form>

        <aside class="flex flex-col gap-3 lg:sticky lg:top-4">
          <div
            class="rounded-2xl border border-n-weak bg-n-alpha-1 p-5 text-sm text-n-slate-11"
          >
            <p class="mb-3 font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.PREVIEW_TITLE') }}
            </p>
            <div
              class="min-h-40 rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 text-sm leading-6 text-n-slate-12 shadow-sm"
            >
              <p class="mb-0 whitespace-pre-wrap">{{ previewMessage }}</p>
            </div>
            <p class="mb-0 mt-3 text-xs text-n-slate-11">
              {{
                t('CAMPAIGN.WHATSAPP.TEMPLATES.PREVIEW_HINT', {
                  variable: contactNameVariable,
                })
              }}
            </p>
          </div>
        </aside>
        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 text-sm text-n-slate-11 lg:col-span-2"
        >
          <p class="mb-2 font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.CREATED_TITLE') }}
          </p>
          <p v-if="!selectedInboxId" class="mb-0">
            {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.SELECT_EMPTY') }}
          </p>
          <p v-else-if="!existingTemplates.length" class="mb-0">
            {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.EMPTY') }}
          </p>
          <div
            v-else
            class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4"
          >
            <div
              v-for="template in existingTemplates"
              :key="`${template.name}-${template.language}`"
              class="relative flex min-w-0 cursor-pointer flex-col rounded-xl border border-n-weak bg-n-solid-1 p-4 transition-colors hover:border-n-brand/40 hover:bg-n-alpha-1"
            >
              <button
                type="button"
                class="absolute inset-0 cursor-pointer rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                :aria-label="`${t('CAMPAIGN.WHATSAPP.TEMPLATES.EDIT')}: ${template.name}`"
                @click="editTemplate(template)"
              />
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0">
                  <p class="mb-1 truncate font-medium text-n-slate-12">
                    {{ template.name }}
                  </p>
                  <p class="mb-0 text-xs text-n-slate-11">
                    {{ template.category }}
                    {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.META_SEPARATOR') }}
                    {{ template.language }}
                    {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.META_SEPARATOR') }}
                    {{
                      template.parameter_format === VARIABLE_FORMATS.POSITIONAL
                        ? t(
                            'CAMPAIGN.WHATSAPP.TEMPLATES.VARIABLE_FORMATS.NUMBER'
                          )
                        : t('CAMPAIGN.WHATSAPP.TEMPLATES.VARIABLE_FORMATS.TEXT')
                    }}
                  </p>
                </div>
                <div class="relative z-10 flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    :title="t('CAMPAIGN.WHATSAPP.TEMPLATES.DELETE')"
                    icon="i-lucide-trash-2"
                    color="ruby"
                    variant="ghost"
                    size="xs"
                    :disabled="
                      isCampaignUsageLoading ||
                      hasNonFinishedCampaigns(template)
                    "
                    @click.stop="deleteTemplate(template)"
                  />
                </div>
              </div>
              <p
                class="mb-0 mt-3 min-h-16 whitespace-pre-wrap rounded-lg bg-n-alpha-1 p-3 text-sm leading-5 text-n-slate-11 line-clamp-3"
              >
                {{
                  template.components?.find(
                    component => component.type === 'BODY'
                  )?.text || '—'
                }}
              </p>
              <p
                v-if="hasNonFinishedCampaigns(template)"
                class="mb-0 mt-2 text-xs text-n-amber-11"
              >
                {{ t('CAMPAIGN.WHATSAPP.TEMPLATES.DELETE_DISABLED') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  </section>
</template>
